import { useState } from "react";

// Spinner shown while a Cloudinary image downloads.
const Spinner = () => (
  <svg
    className="w-8 h-8 animate-spin text-stone-400"
    xmlns="http://www.w3.org/2000/svg"
    fill="none"
    viewBox="0 0 24 24"
    aria-hidden="true"
  >
    <circle
      className="opacity-25"
      cx="12"
      cy="12"
      r="10"
      stroke="currentColor"
      strokeWidth="4"
    />
    <path
      className="opacity-75"
      fill="currentColor"
      d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
    />
  </svg>
);

// Shown if the image fails to load (broken URL, network down, 404, etc.).
const BrokenIcon = () => (
  <svg
    className="w-8 h-8 text-stone-300"
    xmlns="http://www.w3.org/2000/svg"
    fill="none"
    viewBox="0 0 24 24"
    stroke="currentColor"
    strokeWidth="1.5"
    aria-hidden="true"
  >
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      d="M2.25 15.75l5.159-5.159a2.25 2.25 0 013.182 0l5.159 5.159m-1.5-1.5l1.409-1.409a2.25 2.25 0 013.182 0l2.909 2.909m-18 3.75h16.5a1.5 1.5 0 001.5-1.5V6a1.5 1.5 0 00-1.5-1.5H3.75A1.5 1.5 0 002.25 6v12a1.5 1.5 0 001.5 1.5zm10.5-11.25h.008v.008h-.008V8.25zm.375 0a.375.375 0 11-.75 0 .375.375 0 01.75 0z"
    />
  </svg>
);

// Wrapper around <img> for Cloudinary-hosted images. Shows a spinner
// overlay until the image fires onLoad, then fades in the image. On
// error, shows a broken-image icon instead.
//
// The outer <div> is `relative` so the absolute spinner positions
// itself over the image area. Pass `wrapperClassName` to control sizing:
//   - "w-full"         — fill parent width (default; used for Gallery
//                         masonry tiles where the <img> defines height)
//   - "h-full w-full"  — fill parent fully (Collections/ProductPage
//                         carousel, where parent is `aspect-square`)
//   - ""               — size to content (modal lightbox, where the
//                         <img> defines its own max size)
//
// Props mirror <img> plus:
//   wrapperClassName — class on the outer <div> (default: "w-full")
//   imgClassName     — class on the <img> itself (default: none)
//   loading          — passed to <img loading>, default "lazy"
//   decoding         — passed to <img decoding>, default "async"
export const CloudinaryImage = ({
  src,
  alt,
  wrapperClassName = "w-full",
  imgClassName,
  loading = "lazy",
  decoding = "async",
  ...rest
}) => {
  const [status, setStatus] = useState("loading"); // 'loading' | 'loaded' | 'error'

  return (
    <div className={`relative ${wrapperClassName}`}>
      {status === "loading" && (
        <div className="absolute inset-0 flex items-center justify-center bg-stone-50">
          <Spinner />
        </div>
      )}
      {status === "error" && (
        <div className="absolute inset-0 flex items-center justify-center bg-stone-100">
          <BrokenIcon />
        </div>
      )}
      <img
        src={src}
        alt={alt}
        loading={loading}
        decoding={decoding}
        onLoad={() => setStatus("loaded")}
        onError={() => setStatus("error")}
        className={`${imgClassName || ""} transition-opacity duration-300 ${
          status === "loaded" ? "opacity-100" : "opacity-0"
        }`}
        {...rest}
      />
    </div>
  );
};

export default CloudinaryImage;
